import type { AudienceSegment } from '@/features/marketing-shared';
import type { Segmentation, SegmentationCreatePayload } from '@/shared/types/segmentation';
import { createSegmentation } from '../api/use-segmentations';

export function parseSerialCsv(text: string): string[] {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length === 0) return [];
  const split = (line: string) => line.split(/[;,\t]/).map((cell) => cell.trim().replace(/^"|"$/g, ''));
  const header = split(lines[0]).map((cell) => cell.toLowerCase());
  const index = header.findIndex((cell) => cell === 'serial_number' || cell === 'serialnumber' || cell === 'serial');
  const start = index >= 0 ? 1 : 0;
  const column = index >= 0 ? index : 0;
  const serials = lines.slice(start).map((line) => split(line)[column]).filter((cell): cell is string => Boolean(cell));
  return [...new Set(serials)];
}

export async function serialsFromUpload(file: File): Promise<string[]> {
  const name = file.name.toLowerCase();
  if (!name.endsWith('.csv') && !name.endsWith('.txt')) return [];
  return parseSerialCsv(await file.text());
}

function storageKey(idPassbook: string) {
  return `fixe.uploaded-audiences.${idPassbook}`;
}

export function readLocalAudiences(idPassbook: string): Segmentation[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(storageKey(idPassbook));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as Segmentation[]) : [];
  } catch {
    return [];
  }
}

function rememberLocalAudience(segmentation: Segmentation) {
  if (typeof localStorage === 'undefined') return;
  const current = readLocalAudiences(segmentation.id_passbook).filter((item) => item.nome !== segmentation.nome);
  localStorage.setItem(storageKey(segmentation.id_passbook), JSON.stringify([segmentation, ...current]));
}

export function mergeUploadedAudiences(idPassbook: string, remote: Segmentation[]): Segmentation[] {
  const names = new Set(remote.map((item) => item.nome));
  const ids = new Set(remote.map((item) => item._id));
  const extra = readLocalAudiences(idPassbook).filter((item) => !ids.has(item._id) && !names.has(item.nome));
  return [...extra, ...remote];
}

export function segmentationAsAudience(segmentation: Segmentation): AudienceSegment {
  const count = segmentation.leadCount ?? segmentation.resolvedSerialNumbers?.length ?? 0;
  return {
    id: segmentation._id,
    name: segmentation.nome,
    description: segmentation.descricao || 'Lista enviada por arquivo.',
    total: count,
    eligible: count,
    excludedNoConsent: 0,
    excludedUnsubscribed: 0,
    excludedInvalid: 0,
  };
}

export async function materializeUploadedAudience(input: {
  idPassbook: string;
  nome: string;
  fileName: string;
  serials: string[];
}): Promise<{ segmentation: Segmentation; persisted: boolean; parsed: boolean }> {
  const now = new Date().toISOString();
  const local: Segmentation = {
    _id: `upload-${Date.now().toString(36)}`,
    id_passbook: input.idPassbook,
    nome: input.nome.trim(),
    descricao: `Upload ${input.fileName}`,
    status: 'active',
    criteria: { groups: [], connectors: [] },
    resolvedSerialNumbers: input.serials,
    leadCount: input.serials.length,
    lastResolvedAt: now,
    criteriaChangedAt: now,
  };
  rememberLocalAudience(local);

  const payload: SegmentationCreatePayload = {
    id_passbook: input.idPassbook,
    nome: local.nome,
    descricao: local.descricao,
    criteria: local.criteria,
    uploadedSerials: input.serials,
  };

  try {
    const created = await createSegmentation(payload);
    return { segmentation: created, persisted: true, parsed: input.serials.length > 0 };
  } catch {
    return { segmentation: local, persisted: false, parsed: input.serials.length > 0 };
  }
}
